import { useCallback, useEffect, useRef, useState } from "react";

const MIME = () => (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported?.("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : "audio/mp4");
const MOBILE = /Android|iPhone|iPad|iPod/i.test(typeof navigator !== "undefined" ? navigator.userAgent : "");

// Mic capture with silence detection: each spoken phrase becomes one audio blob (no audio is stored).
export function useVoiceCapture(onSegment) {
  const [state, setState] = useState("off");
  const [interim, setInterim] = useState("");
  const [level, setLevel] = useState(0);
  const r = useRef({});
  const cb = useRef(onSegment);
  cb.current = onSegment;

  const newRecorder = useCallback(() => {
    const s = r.current;
    const rec = new MediaRecorder(s.stream, { mimeType: MIME() });
    const chunks = [];
    rec.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    rec.onstop = () => { if (rec.keep && chunks.length) cb.current(new Blob(chunks, { type: rec.mimeType || MIME() })); };
    rec.start(250); s.rec = rec; s.spoke = false; s.t0 = Date.now();
  }, []);

  const cut = useCallback((keep) => {
    const s = r.current;
    if (s.rec && s.rec.state !== "inactive") { s.rec.keep = keep; s.rec.stop(); }
    if (s.stream && !s.paused) newRecorder();
  }, [newRecorder]);

  const stop = useCallback(() => {
    const s = r.current;
    clearInterval(s.tick); s.sr?.abort?.();
    if (s.rec && s.rec.state !== "inactive") { s.rec.keep = false; s.rec.stop(); }
    s.stream?.getTracks().forEach((t) => t.stop()); s.ctx?.close?.();
    r.current = {}; setState("off"); setInterim(""); setLevel(0);
  }, []);

  const start = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") throw new Error("unsupported");
    const stream = await navigator.mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const an = ctx.createAnalyser(); an.fftSize = 1024; ctx.createMediaStreamSource(stream).connect(an);
    const buf = new Uint8Array(an.fftSize);
    r.current = { stream, ctx, paused: false };
    newRecorder();
    r.current.tick = setInterval(() => {
      const s = r.current;
      if (!s.stream || s.paused) return;
      an.getByteTimeDomainData(buf);
      const rms = Math.sqrt(buf.reduce((a, v) => a + ((v - 128) / 128) ** 2, 0) / buf.length);
      setLevel(Math.min(1, rms * 6));
      const now = Date.now();
      if (rms > 0.035) { s.spoke = true; s.last = now; }
      if (s.spoke && now - s.last > 1300) cut(true);
      else if (s.spoke && now - s.t0 > 30000) cut(true);
      else if (!s.spoke && now - s.t0 > 15000) cut(false);
    }, 100);
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SR && !MOBILE) {
      const sr = new SR(); sr.lang = "pt-BR"; sr.interimResults = true; sr.continuous = true;
      sr.onresult = (e) => setInterim(Array.from(e.results).slice(e.resultIndex).map((x) => x[0].transcript).join(" "));
      sr.onend = () => { if (r.current.sr === sr && !r.current.paused) try { sr.start(); } catch { /* restarted */ } };
      try { sr.start(); r.current.sr = sr; } catch { /* live text optional */ }
    }
    setState("listening");
  }, [cut, newRecorder]);

  const pause = useCallback(() => {
    const s = r.current; if (!s.stream) return;
    s.paused = true; s.sr?.stop?.();
    if (s.rec && s.rec.state !== "inactive") { s.rec.keep = false; s.rec.stop(); }
    setState("paused"); setInterim(""); setLevel(0);
  }, []);
  const resume = useCallback(() => {
    const s = r.current; if (!s.stream) return;
    s.paused = false; newRecorder(); try { s.sr?.start?.(); } catch { /* already running */ }
    setState("listening");
  }, [newRecorder]);

  useEffect(() => stop, [stop]);
  return { state, interim, setInterim, level, start, stop, pause, resume };
}
